import { useMemo, useRef, useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';

import { buildRegulationTopics, RegulationTopic } from 'components/regulamento-content/regulation-topics';

import './regulation-reader.scss';

interface RegulationReaderProps {
  /** `regulation_text`. Sem ele, o leitor mostra o carregamento. */
  html?: string;
}

/**
 * Recuo dos subtópicos por nível do título, como no design (h3 = 26px, h4 em diante = 38px).
 * D3: 26px e 38px não têm degrau no Tailwind.
 */
const SUBTOPIC_INDENT: Record<number, string> = { 3: 'pl-[26px]' };
const DEEP_SUBTOPIC_INDENT = 'pl-[38px]';

// D3: 9px não tem degrau no Tailwind.
const TOC_ITEM =
  'flex w-full items-center gap-2 rounded-lg py-[9px] pr-2.5 text-left leading-jj-caption hover:bg-[rgba(70,94,255,0.08)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue';

/** Espaço entre o topo da área de texto e o título de destino. */
const SCROLL_OFFSET = 12;

/**
 * Texto do regulamento com a estrutura de tópicos ao lado, como no modal do `Home.dc.html`.
 * Tópico com subtópicos abre e fecha a lista. Tópico sem subtópicos e subtópico rolam o texto
 * até o título. Abaixo de 1024px, a estrutura sai da lateral e fica recolhida sobre o texto.
 */
export function RegulationReader({ html }: RegulationReaderProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [openTopics, setOpenTopics] = useState<Record<string, boolean>>({});
  const [tocOpen, setTocOpen] = useState(false);

  const regulation = useMemo(() => (html ? buildRegulationTopics(html) : undefined), [html]);

  if (!regulation) {
    return (
      <p role="status" className="m-0 p-8 text-center text-sm text-content-soft">
        Carregando regulamento…
      </p>
    );
  }

  function scrollToHeading(id: string) {
    const box = contentRef.current;
    const heading = box?.querySelector<HTMLElement>(`[id="${id}"]`);
    if (!box || !heading) return;

    box.scrollTop = heading.offsetTop - SCROLL_OFFSET;
    setTocOpen(false);
  }

  function toggleTopic(id: string) {
    setOpenTopics(current => ({ ...current, [id]: !current[id] }));
  }

  function renderTopic(topic: RegulationTopic) {
    const hasSubtopics = topic.subtitles.length > 0;
    const isOpen = !!openTopics[topic.id];
    const listId = `toc-${topic.id}`;

    return (
      <li key={topic.id}>
        <button
          type="button"
          title={topic.title}
          aria-expanded={hasSubtopics ? isOpen : undefined}
          aria-controls={hasSubtopics && isOpen ? listId : undefined}
          onClick={() => (hasSubtopics ? toggleTopic(topic.id) : scrollToHeading(topic.id))}
          className={`${TOC_ITEM} mb-0.5 pl-2.5 text-jj-body-sm font-bold text-brand-blue-dark`}
        >
          <FiChevronRight
            size={12}
            strokeWidth={3}
            aria-hidden="true"
            className={`shrink-0 transition-transform duration-200 ease-jj-standard ${hasSubtopics ? '' : 'invisible'} ${
              isOpen ? 'rotate-90' : ''
            }`}
          />
          <span className="min-w-0 flex-1 truncate">{topic.title}</span>
        </button>

        {hasSubtopics && isOpen && (
          <ul id={listId} className="m-0 list-none p-0">
            {topic.subtitles.map(subtopic => (
              <li key={subtopic.id}>
                <button
                  type="button"
                  title={subtopic.title}
                  onClick={() => scrollToHeading(subtopic.id)}
                  className={`${TOC_ITEM} mb-0.5 text-jj-label text-content-soft ${
                    SUBTOPIC_INDENT[subtopic.level] ?? DEEP_SUBTOPIC_INDENT
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{subtopic.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  const hasTopics = regulation.titles.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      {hasTopics && (
        <button
          type="button"
          aria-expanded={tocOpen}
          aria-controls="regulation-toc"
          onClick={() => setTocOpen(open => !open)}
          className="flex shrink-0 items-center justify-between gap-2 border-b border-surface-200 bg-surface-subtle px-5 py-3 font-display text-xs font-bold uppercase tracking-[.05em] text-brand-blue-dark lg:hidden"
        >
          Estrutura de tópicos
          <FiChevronRight
            size={14}
            strokeWidth={3}
            aria-hidden="true"
            className={`transition-transform duration-200 ease-jj-standard ${tocOpen ? 'rotate-90' : ''}`}
          />
        </button>
      )}

      {hasTopics && (
        <nav
          id="regulation-toc"
          aria-label="Estrutura de tópicos"
          // D3: 290px não tem degrau no Tailwind.
          className={`${
            tocOpen ? 'block' : 'hidden'
          } max-h-[45%] shrink-0 overflow-y-auto border-b border-surface-200 bg-surface-subtle px-4 pb-6 pt-3 lg:block lg:max-h-none lg:w-[290px] lg:border-b-0 lg:border-r lg:pt-5`}
        >
          <p
            aria-hidden="true"
            className="mx-2 mb-3 mt-0 hidden font-display text-xs font-bold uppercase tracking-[.05em] text-brand-blue-dark lg:block"
          >
            Estrutura de tópicos
          </p>
          <ul className="m-0 list-none p-0">{regulation.titles.map(renderTopic)}</ul>
        </nav>
      )}

      <div
        ref={contentRef}
        tabIndex={0}
        aria-label="Texto do regulamento"
        className="regulation-text relative min-h-0 min-w-0 flex-1 overflow-y-auto scroll-smooth px-5 pb-10 pt-5 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-blue lg:px-8 lg:pt-6"
        dangerouslySetInnerHTML={{ __html: regulation.html }}
      />
    </div>
  );
}
